import { QueryClient } from '@tanstack/react-query'

/**
 * QueryClient configuration optimized for GraphQL queries
 *
 * Key features:
 * - Aggressive caching with 5-minute staleTime to reduce network requests
 * - 10-minute cache time to keep data in memory
 * - Automatic retry with exponential backoff for failed queries
 * - Disabled automatic refetching on window focus (can be enabled per-query)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      // During this time, no background refetches will occur
      staleTime: 5 * 60 * 1000, // 5 minutes

      // Cache data for 10 minutes after it becomes unused
      // This allows for instant navigation back to cached pages
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)

      // Retry failed queries 3 times with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Disable automatic refetching on window focus by default
      // This prevents unnecessary network requests when users switch tabs
      // Can be overridden per-query if needed
      refetchOnWindowFocus: false,

      // Disable automatic refetching on reconnect by default
      // Can be overridden for critical queries
      refetchOnReconnect: false,

      // Disable refetch on mount for stale queries by default
      // This prevents duplicate requests when components mount
      refetchOnMount: false,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      retryDelay: 1000,
    },
  },
})

/**
 * Query key factory for consistent cache key generation
 *
 * Usage:
 * - queryKeys.dashboard.all -> ['dashboard']
 * - queryKeys.dashboard.detail(id) -> ['dashboard', id]
 * - queryKeys.user.profile() -> ['user', 'profile']
 */
export const queryKeys = {
  dashboard: {
    all: ['dashboard'] as const,
    pending: ['dashboard', 'pending'] as const,
    approved: ['dashboard', 'approved'] as const,
    rejected: ['dashboard', 'rejected'] as const,
  },
  user: {
    all: ['user'] as const,
    profile: () => ['user', 'profile'] as const,
    details: (id: string) => ['user', 'details', id] as const,
  },
  leave: {
    all: ['leave'] as const,
    list: (filters?: Record<string, unknown>) => ['leave', 'list', filters] as const,
    details: (id: string) => ['leave', 'details', id] as const,
  },
  company: {
    all: ['company'] as const,
    details: () => ['company', 'details'] as const,
    settings: () => ['company', 'settings'] as const,
  },
  app: {
    all: ['app'] as const,
    companyAndUser: (userId: string) => ['app', 'companyAndUser', userId] as const,
    userActionFlow: (type?: string) => ['app', 'userActionFlow', type] as const,
    userHelpInfo: () => ['app', 'userHelpInfo'] as const,
  },
  rechargeType: {
    all: ['rechargeType'] as const,
    userRechargeType: () => ['rechargeType', 'user'] as const,
    gameColleagues: (limit?: number) => ['rechargeType', 'game', 'colleagues', limit] as const,
    companyCount: () => ['rechargeType', 'company', 'count'] as const,
    leaderboard: (limit?: number) => ['rechargeType', 'game', 'leaderboard', limit] as const,
    ptoLeaveTypeNames: () => ['rechargeType', 'ptoLeaveTypeNames'] as const,
  },
  // Add more query keys as needed for other domains
} as const
